/**
 * Messaging Service
 * Wraps the /api/conversations endpoints.
 */

import apiClient from './api';

const messagingService = {
  /**
   * List all conversations for the logged-in user.
   * Returns { conversations: [...] }
   */
  getConversations: async () => {
    const { data } = await apiClient.get('/conversations');
    return data;
  },

  /**
   * Find an existing conversation with participantId, or create one.
   * @param {string} participantId  UUID of the other user
   * @param {string|null} projectId Optional project id
   * Returns the conversation object.
   */
  getOrCreateConversation: async (participantId, projectId = null) => {
    const payload = { participant_id: participantId };
    if (projectId) payload.project_id = projectId;
    const { data } = await apiClient.post('/conversations', payload);
    return data;
  },

  /**
   * Get paginated messages for a conversation.
   * Marks messages as read (updates last_read_at).
   */
  getMessages: async (conversationId, page = 1) => {
    const { data } = await apiClient.get(`/conversations/${conversationId}/messages`, {
      params: { page, limit: 50 }
    });
    return data;
  },

  /**
   * Send a message in a conversation.
   * @param {number} conversationId
   * @param {string} content
   * @param {File|null} attachment  — a real File object from an <input type="file">
   * @param {function|null} onUploadProgress — optional (percent) => void
   * @param {number|null} replyToMessageId — quote another message in this conversation
   */
  sendMessage: async (conversationId, content, attachment = null, onUploadProgress = null, replyToMessageId = null) => {
    // If there's a file, send as multipart/form-data so the backend uploads to S3
    if (attachment && attachment instanceof File) {
      const formData = new FormData();
      formData.append('content', content || '');
      formData.append('attachment', attachment);
      if (replyToMessageId) formData.append('reply_to_message_id', replyToMessageId);
      const config = {};
      if (onUploadProgress) {
        config.onUploadProgress = (e) => {
          const pct = Math.round((e.loaded * 100) / (e.total || 1));
          onUploadProgress(pct);
        };
      }
      // Let axios auto-detect Content-Type for FormData (includes boundary).
      // Explicitly setting 'multipart/form-data' strips the boundary and breaks multer parsing.
      const { data } = await apiClient.post(
        `/conversations/${conversationId}/messages`,
        formData,
        { ...config, headers: { 'Content-Type': undefined } }
      );
      return data;
    }

    // Text-only — plain JSON (backward compatible)
    const payload = { content };
    if (replyToMessageId) payload.reply_to_message_id = replyToMessageId;
    const { data } = await apiClient.post(`/conversations/${conversationId}/messages`, payload);
    return data;
  },

  /**
   * Get number of conversations with unread messages.
   * Returns { unread_conversations: N }
   */
  getUnreadCount: async () => {
    const { data } = await apiClient.get('/conversations/unread-count');
    return data;
  },

  /**
   * Delete / leave a conversation.
   * Removes the current user from the thread; if both sides delete, it's purged.
   */
  deleteConversation: async (conversationId) => {
    const { data } = await apiClient.delete(`/conversations/${conversationId}`);
    return data;
  },

  /**
   * Search users by name for the New Message dialog.
   * Returns { users: [...] }
   */
  searchUsers: async (query) => {
    const { data } = await apiClient.get('/conversations/search-users', { params: { q: query } });
    return data;
  },

  /**
   * WhatsApp chat-line info for the "get these chats on WhatsApp" banner.
   * Returns { enabled, number, display, waLink } — enabled:false until the
   * platform's public WhatsApp number is configured.
   */
  getWhatsAppInfo: async () => {
    const { data } = await apiClient.get('/whatsapp/info');
    return data.data;
  }
};

export default messagingService;
